import { NavigateFunction } from 'react-router-dom';
import { AuthModel } from '../../auth/_models';
import {
  checkMFAStatus,
  createLoginToken,
  getBuildingDetails,
  getIDVStatus,
  getListMFAEnabled,
  getTenantDetails,
} from '../../auth/_requests';
import { ModalState, tenantProgressRespone } from './types';
import { showToast } from './showToast';
import { getPostLoginRouteForDeepLink } from './deepLink';
import { shouldShowIdvChangeRequest } from './idvStatus';
import { hasUsableBuildingAddress } from './payFlowGuard';
import { pushStorage } from '../webPush/pushNotificationStorage';

export const postLoginTask = async (
  auth: AuthModel,
  navigate: NavigateFunction,
  saveAuth: (auth: AuthModel | undefined) => void
) => {
  try {
    const tenantResponse = await getTenantDetails(auth?.data.token);
    const response = await checkMFAStatus(auth.data.token);
    const mfaReseponse = await getListMFAEnabled(auth.data.token);
    if (response?.data.isMfaRequired && tenantResponse.data.user.isEmailVerified) {
      const emailMFAMap = mfaReseponse?.data.find((mfa) => mfa.name === 'email');
      const phoneMFAMap = mfaReseponse?.data.find((mfa) => mfa.name === 'phoneNumber');
      const emailMFA = String(emailMFAMap?.isEnabled) === 'true' ? true : false;
      const phoneMFA = String(phoneMFAMap?.isEnabled) === 'true' ? true : false;

      if (emailMFA && phoneMFA) {
        navigate('/select-two-factor-authentication');
      } else if (emailMFA) {
        navigate('/two-factor-authentication/email');
      } else navigate('/two-factor-authentication/phone');
      return;
    } else {
      try {
        const emailVerified = tenantResponse.data.user.isEmailVerified === 1;
        const isBuilding = tenantResponse.data.tenantBuildings.length;
        const isDeclined =
          tenantResponse.data.tenant.tenantIdv &&
          tenantResponse.data.tenant.tenantIdv.status === 'declined';
        const isIndividualAccount =
          tenantResponse?.data?.tenant?.accountType === 'personal' ||
          tenantResponse?.data?.tenant?.accountType === 'both';
        switch (true) {
          case !emailVerified:
            navigate('/verify-email');
            break;
          case !tenantResponse.data.user.acquisitionSource:
            navigate('/acquisition-source');
            break;
          case !isIndividualAccount && !isBuilding:
            navigate('/kyb');
            break;
          case isDeclined:
            showToast({
              message: 'Your identity verification was declined. Please contact support.',
              type: 'error',
            });
            saveAuth(undefined);
            return;
            break;

          default: {
            const deepLinkRoute = getPostLoginRouteForDeepLink();
            navigate(deepLinkRoute ?? '/dashboard');
            break;
          }
        }
      } catch (error) {
        console.error('Error fetching tenant details:', error);
      }
      try {
        const response = await createLoginToken(auth?.data.token || '');
        const loginToken = response.data.loginToken;
        const newAuth = {
          message: 'success',
          data: {
            tenantId: auth?.data.tenantId || '',
            token: loginToken,
          },
        };
        saveAuth(newAuth);
      } catch (error) {
        console.error('Error creating login token:', error);
      }
    }
  } catch (error) {
    console.error('Error checking MFA status:', error);
  }
};

const openIdvStatusModal = (
  setModal: (modal: string | null, state?: ModalState) => void,
  tenantBuildingId: string,
  pendingIdvAuthHold: boolean,
  statusData?: {
    status?: string | null;
    tenantIdvStatus?: string;
    isNewIdvRequired?: boolean;
    messageFromAdmin?: string | null;
    timestampCompleted?: string | Date | null;
  },
  userId?: number
) => {
  const { status, tenantIdvStatus, isNewIdvRequired, messageFromAdmin, timestampCompleted } = statusData ?? {};

  if (
    !pendingIdvAuthHold &&
    shouldShowIdvChangeRequest({ tenantIdvStatus, timestampCompleted })
  ) {
    setModal('idv-change-request', {
      tenantBuildingId,
      pendingIdvAuthHold,
      messageFromAdmin,
      isNewIdvRequired,
    });
    return;
  }

  if (
    tenantIdvStatus === 'declined' ||
    status === 'AUTO_REJECTED' ||
    status === 'MANUAL_REJECTED'
  ) {
    setModal('idv-declined', { pendingIdvAuthHold });
    return;
  }

  if (
    tenantIdvStatus === 'approved' ||
    status === 'AUTO_APPROVED' ||
    status === 'MANUAL_APPROVED' ||
    status === 'approved'
  ) {
    if (userId != null) {
      pushStorage.setHasCompletedIdv(userId);
    }
    setModal('idv-verification-success', {
      tenantBuildingId,
      pendingIdvAuthHold,
    });
    return;
  }

  setModal('identity-verification', {
    tenantBuildingId,
    refreshProfile: true,
    pendingIdvAuthHold,
  });
};

export const tenantNeedsPreIdvDetails = async (
  token: string
): Promise<{ needsDetails: boolean; firstBuildingId: string }> => {
  const [tenantRes, buildingRes] = await Promise.all([
    getTenantDetails(token),
    getBuildingDetails(token),
  ]);
  const occupation = tenantRes?.data?.tenant?.occupation?.trim();
  const firstBuilding = buildingRes?.data?.[0];
  const firstBuildingId = firstBuilding?.building?.tenantBuildingId || '';
  const hasAddress = hasUsableBuildingAddress(firstBuilding?.address);
  return {
    needsDetails: !occupation || !hasAddress,
    firstBuildingId,
  };
};

export const openIdentityVerificationModal = async (
  setModal: (modal: string | null, state?: ModalState) => void,
  token: string,
  tenantBuildingId: string,
  options?: { pendingIdvAuthHold?: boolean; skipPrerequisiteDetailsCheck?: boolean; userId?: number }
) => {
  const pendingIdvAuthHold = options?.pendingIdvAuthHold === true;
  const userId = options?.userId;
  const buildingId = tenantBuildingId;

  try {
    // Fetch IDV status (Plaid session) + tenant details (for timestampCompleted / messageFromAdmin)
    const [idvResponse, tenantResponse] = await Promise.all([
      getIDVStatus(token).catch(() => null),
      getTenantDetails(token).catch(() => null),
    ]);

    const tenant = tenantResponse?.data?.tenant;
    const tenantIdv = tenant?.tenantIdv;

    const isAlreadyApproved =
      tenant?.isIdentityVerified === 1 ||
      (tenant?.isIdentityVerified as unknown) === true ||
      tenantIdv?.status === 'approved' ||
      idvResponse?.data?.status === 'AUTO_APPROVED' ||
      idvResponse?.data?.status === 'MANUAL_APPROVED' ||
      idvResponse?.data?.status === 'approved' ||
      idvResponse?.data?.tenantIdvStatus === 'approved';

    if (isAlreadyApproved) {
      if (userId != null) {
        pushStorage.setHasCompletedIdv(userId);
      }
      setModal('idv-verification-success', {
        tenantBuildingId: buildingId,
        pendingIdvAuthHold,
      });
      return;
    }

    if (idvResponse && 'data' in idvResponse && idvResponse.data) {
      openIdvStatusModal(
        setModal,
        buildingId,
        pendingIdvAuthHold,
        {
          ...idvResponse.data,
          // Overlay timestampCompleted and messageFromAdmin from getTenantDetails
          // because getIDVStatus does not return these fields
          timestampCompleted: tenantIdv?.timestampCompleted ?? null,
          messageFromAdmin: tenantIdv?.messageFromAdmin ?? null,
          isNewIdvRequired: idvResponse.data.isNewIdvRequired ?? tenantIdv?.isNewIdvRequired,
        },
        userId
      );
      return;
    }

    setModal('identity-verification', {
      tenantBuildingId: buildingId,
      refreshProfile: true,
      pendingIdvAuthHold,
    });
  } catch {
    console.error('Error fetching IDV status');
    setModal('identity-verification', {
      tenantBuildingId: buildingId,
      refreshProfile: true,
      pendingIdvAuthHold,
    });
  }
};

export const handleModalNavigation = async (
  profileData: tenantProgressRespone | null,
  tenantBuildingId: string,
  setModal: (modal: string | null, state?: ModalState) => void,
  token: string
) => {
  switch (true) {
    case !profileData?.data.steps.completeBuilding:
      setModal('complete-building', { tenantBuildingId: tenantBuildingId });
      break;
    case !profileData?.data.steps.isIdentityVerified:
    case !profileData?.data.steps.addedBuildingAddress:
      await openIdentityVerificationModal(setModal, token, tenantBuildingId);
      break;
    default:
      setModal('complete-building');
  }
};
